import PropTypes from 'prop-types';
import React from 'react';
import { merge, sortBy } from 'lodash';
import api from '../../../api/Entities.api';
import categoriesApi from './../../../api/Categories.api';
import { FormattedMessage } from 'react-intl';
import { SORT } from './../../../constants';
import PillList from './../../Form/PillList';
import { BoldedCell } from '../Components';
import TableCoreComponent from './../TableCoreComponent';
import withInfiniteScroll from './../hoc/Scrollable';
import withSort from './../hoc/Sortable';
import withSearch from './../hoc/Searchable';
import withSelection from './../hoc/Selectable';
import LightTable from './../VeryLightTable';
import { compose } from 'redux';

const Table = compose(
    withSelection,
    withSort,
    withSearch,
    withInfiniteScroll
)(LightTable);

const defaultLoadParams = {
    filters: { query: "" },
    sorting: { sortBy: "companyTitleSort", direction: SORT.ASC },
    pagination: { page: 0 },
    reset: false
};
const pageSize = 50;

export default class EntityTable extends TableCoreComponent {
    static propTypes = {
        onChange: PropTypes.func,
        entityIds: PropTypes.arrayOf(PropTypes.string)
    };

    static defaultProps = {
        entityIds: null
    };
    constructor(props) {
        super(props);
        this.state = {
            categoryNames: {},
            listItems: null,
            currentPage: 0,
            totalCount: 0,
            filterCount: 0,
            loadParams: defaultLoadParams,
            selectedItems: [],
            searchTerm: ""
        };

        this.loadItems = this.loadItems.bind(this);
        this.handleErrorState = this.handleErrorState.bind(this);
        this.onChange = this.onChange.bind(this);
        this.toggleSelectAll = this.toggleSelectAll.bind(this);
    }

    componentDidMount() {
        categoriesApi.getCategoryNames().then((categories) => {
            this.setState({ categoryNames: categories });
            if (this.props.entityIds && this.props.entityIds.length) {
                return api.specific(this.props.entityIds).then(entities => {
                    this.setState({
                        selectedItems: entities.map(entity => this.formatItem(entity))
                    });
                    this.loadItems();
                })
            } else {
                this.loadItems();
            }
        });
    }


    // init load params
    loadParams = defaultLoadParams;

    loadItems(params) {
        this.loadParams = merge({}, this.loadParams, params); // mutates/merges the component loadParams
        let { filters, sorting, reset } = { ...this.loadParams };
        filters.name = filters.query;

        // resets store, needed for searching or sorting
        if (reset) {
            this.setState({
                currentPage: 0,
                listItems: null,
            });
            this.loadParams.reset = false;
        }
        this.setState({ searchTerm: this.loadParams.filters.query })

        return api.count(filters, sorting).then((countRes) => {
            return api.get(filters, sorting, {
                page: this.state.currentPage,
                pageSize: pageSize
            }).then((response) => {
                const items = response.body.map(item => {
                    item.busy = false;
                    return item;
                });
                if (filters.name && filters.name.length) {
                    this.setState({
                        currentPage: ++this.state.currentPage,
                        listItems: (this.state.listItems || []).concat(items),
                        filterCount: countRes.body
                    });
                } else {
                    this.setState({
                        currentPage: ++this.state.currentPage,
                        listItems: (this.state.listItems || []).concat(items),
                        filterCount: countRes.body,
                        totalCount: countRes.body
                    });
                }

                return this.state.listItems
            })
        }).catch(this.handleErrorState);
    }

    /**
     * View function for items in table
     * @param item
     * @param searchTerm
     * @returns {{key, companyTitle: XML, active: string, duns: (string|string|*), categoryIDs: XML, object: *}}
     */
    formatItem = (item, searchTerm = "") => {
        return {
            key: item.id,
            companyTitle: <BoldedCell value={item.companyTitle} searchTerm={searchTerm} />,
            active: <span className={"label " + (item.active ? "active" : "inactive")}>
                <FormattedMessage id={"General_CoreFunctionality_UIText_general." + (item.active ? "active" : "inactive")}/>
            </span>,
            duns: <BoldedCell value={item.dunsNumber} searchTerm={searchTerm} />,
            categoryIDs: <PillList items={item.categoryIDs.map(catId => {
                return {
                    label: this.state.categoryNames[catId]
                }
            })} visibleItemCount={5}/>,
            object: item
        }
    };

    /**
     * View function for item in selected box
     * @param item
     */
    formatSelectedItem = (item) => <span>{item.companyTitle}</span>;

    /**
     * Handler for selection change
     * @param selectedItems
     */
    onChange = (selectedItems) => {
        this.setState({ selectedItems });
        this.props.onChange(selectedItems.map(item => item.object.id));
    };

    toggleSelectAll() {
        if (this.state.selectedItems.length === this.state.totalCount) {
            this.onChange([])
        } else {
            api.get({ name: null }, defaultLoadParams.sorting, { page: -1, pageSize: -1 }).then(items => {
                const allItems = items.body;
                if (allItems.length) {
                    this.onChange(allItems.map(item => this.formatItem(item)));
                }
            })
        }
    }

    handleChangeSearchTerm(searchTerm) {
        this.setState({ searchTerm })
    }

    /**
     * Resets state in case of error
     */
    handleErrorState(e) {
        console.log('Error caught', e);
        this.setState({
            listItems: [],
            totalCount: 0,
            itemCount: 0,
            hasError: true
        })
    }

    headerItems = [
        {
            key: "companyTitle",
            label: <FormattedMessage id={"Entities_Review_Error_errorCompanies.CompanyName"}/>,
            sortable: true
        },
        {
            key: "active",
            label: <FormattedMessage id={"Entities_Edit_UIText_thirdPartyListing.statusColumnHeading"}/>,
            sortable: true
        },
        {
            key: "categoryIDs",
            label: <FormattedMessage id={"Entities_Edit_UIText_thirdPartyListing.categoriesColumnHeading"}/>,
        },
        {
            key: "duns",
            label: <FormattedMessage id={"Entities_Review_Error_errorCompanies.CompanyDuns"}/>
        }
    ];

    defaultSort = { sortBy: "companyTitleSort", direction: SORT.ASC };

    render = () =>
        <div className='selectable-table entity-table-temp'>
            <Table  headerItems={this.headerItems}
                    listItems={this.state.listItems ? this.state.listItems.map(item => this.formatItem(item, this.state.searchTerm)) : null}
                    noEntries={this.noEntries}
                    loadItems={this.loadItems}
                    totalCount={this.state.totalCount}
                    filterCount={this.state.filterCount}
                    itemCount={this.state.filterCount}
                    defaultSort={this.defaultSort}
                    onSearchTermChange={this.handleChangeSearchTerm}
                    searchTerm={this.state.searchTerm}
                    onSelectionChange={this.onChange}
                    selectedItems={sortBy(this.state.selectedItems, item => item.companyTitle.props.value.toLowerCase())}
                    selectAllToggle={this.toggleSelectAll}
                    formatSelectedItem={this.formatSelectedItem} />
        </div>
}


// WEBPACK FOOTER //
// ./src/scripts/reusable/LightTable/specific/EntityTable.jsx